import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import { formatPhoneNumber } from '../../utils/formatPhone'
import { Client, messagesApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'

interface QuickSmsDialogProps {
  open: boolean
  onClose: () => void
  client: Client | null
}

export default function QuickSmsDialog({ open, onClose, client }: QuickSmsDialogProps) {
  const { showSuccess, showError } = useNotification()
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    if (!client || !message.trim()) return

    setSending(true)
    try {
      await messagesApi.send(client.id, message.trim())
      showSuccess('SMS sent successfully')
      setMessage('')
      onClose()
    } catch (err) {
      console.error('Error sending SMS:', err)
      showError(err instanceof Error ? err.message : 'Failed to send SMS')
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    if (!sending) {
      setMessage('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send SMS to {client?.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sending to: {formatPhoneNumber(client?.phone)}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Message"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !message.trim()}
        >
          {sending ? <CircularProgress size={24} color="inherit" /> : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
